import Clickable from 'components/Common/Clickable'
import IconButton from 'LuxKit/Button/IconButton'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import { prettyFlexibleDates } from 'components/Search/SearchForm/SearchDateInput/FlexibleDateSearchField'
import { prettyDates, getTotalRoomsString } from 'components/Search/utils'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { formatOccupantsAsGuests } from 'lib/offer/occupancyUtils'
import config from 'constants/config'
import { rem } from 'polished'
import type { ComponentProps } from 'react'
import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import styled from 'styled-components'
import AIGlowBorder from 'components/Search/SearchForm/AIGlowBorder'
import HotelExposedFiltersHeader from './HotelExposedFiltersHeader/HotelExposedFiltersHeader'
import { deleteSearchParams } from 'lib/url/searchUrlUtils'
import type { OfferListSortOption } from 'constants/offerListFilters'

const DESCRIPTION_SEPARATOR = '•'

const Container = styled.div`
  padding: ${rem(8)} ${rem(20)} ${rem(12)};
  > * + * {
    margin-top: ${rem(12)};
  }
`

const InlineContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  > * + * {
    margin-left: ${rem(8)};
  }
`

const EditContainer = styled(Clickable)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${rem(8)};
  width: 100%;
  min-width: 0;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const DescriptionSeparator = styled.span`
  margin: 0 ${rem(8)};
`

const StyledLineSearchIcon = styled(LineSearchIcon)`
  flex: 0 0 auto;
`

const StyledIconButton = styled(IconButton)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

function Description({ primaryText, secondaryText }: { primaryText: string, secondaryText?: string }) {
  if (!secondaryText) {
    return <Caption variant="large" wrap="truncate">{primaryText}</Caption>
  }

  return (
    <Caption variant="large" wrap="truncate">
      {primaryText}
      <DescriptionSeparator>{DESCRIPTION_SEPARATOR}</DescriptionSeparator>
      {secondaryText}
    </Caption>
  )
}

interface MappedStateProps {
  windowSearch: string
}

type InheritedHotelExposedFiltersHeaderProps = Pick<ComponentProps<typeof HotelExposedFiltersHeader>, 'onSortChange' | 'onFilterChange'>

interface Props extends InheritedHotelExposedFiltersHeaderProps {
  onEdit: () => void
  filters: App.OfferListFilters
  sortOptions?: Array<OfferListSortOption>
  className?: string
  hideFilters?: boolean
  overrideAvailableFilters?: App.OfferListFilterOptions
  showFilters?: boolean
  isMapView?: boolean
}

function SearchField(props: Props & MappedStateProps) {
  const {
    onEdit,
    filters,
    windowSearch,
    sortOptions,
    className,
    overrideAvailableFilters,
    onFilterChange,
    onSortChange,
    showFilters = true,
    isMapView = false,
  } = props
  const intl = useIntl()
  const showAIGlow = config.NATURAL_LANGUAGE_SEARCH_ENABLED

  const {
    checkinDate,
    checkoutDate,
    occupancies,
    searchTargetLabel,
    dateSearchOptionId,
    flexibleMonths,
    flexibleNights,
    userSelectedFlexibleMonths,
  } = useContext(GlobalSearchStateContext)

  const mapViewURL = useMemo(() => `/search/map?${deleteSearchParams(windowSearch, 'sortBy')}`, [windowSearch])

  const anytimeText = intl.formatMessage({
    id: 'X3uzzIDkx0fX22D7UmQU',
    defaultMessage: 'Anytime',
    description: 'Label shown when no specific date is selected for search',
  })

  const mapAreaText = intl.formatMessage({
    id: 'xP3ZFVMbgOILlHR2q-Ll',
    defaultMessage: 'Map area',
    description: 'Fallback label when no search target location is specified',
  })

  const mapText = intl.formatMessage({
    id: 'dgZ0q4-mlU-8LQv1Cntf',
    defaultMessage: 'Map',
    description: 'Button label to switch to the map view display in the search results interface',
  })

  const searchBarDescription = useMemo(() => {
    if (dateSearchOptionId === DATE_SEARCH_OPTION_IDS.ANYTIME) {
      return {
        primaryText: anytimeText,
        secondaryText: undefined,
      }
    }
    const datesDetails = dateSearchOptionId === DATE_SEARCH_OPTION_IDS.FLEXIBLE ?
        prettyFlexibleDates(userSelectedFlexibleMonths ? flexibleMonths : undefined, flexibleNights, undefined, intl) :
        prettyDates(intl, checkinDate, checkoutDate)
    const occupancyWithRoomDetails = occupancies ?
      `${formatOccupantsAsGuests(...occupancies)} ${getTotalRoomsString(intl, occupancies)}` :
      undefined
    return {
      primaryText: datesDetails,
      secondaryText: occupancyWithRoomDetails,
    }
  }, [dateSearchOptionId, userSelectedFlexibleMonths, flexibleMonths, flexibleNights, checkinDate, checkoutDate, occupancies, anytimeText, intl])
  const searchBarContent = (
    <EditContainer onClick={onEdit}>
      <TextContainer>
        <Heading variant="heading6" wrap="truncate">{searchTargetLabel ?? mapAreaText}</Heading>
        <Description primaryText={searchBarDescription.primaryText} secondaryText={searchBarDescription.secondaryText} />
      </TextContainer>
      <StyledLineSearchIcon size={20} />
    </EditContainer>
  )

  return (
    <Container className={className}>
      <InlineContainer>
        {showAIGlow ? <AIGlowBorder>{searchBarContent}</AIGlowBorder> : searchBarContent}
        {!isMapView && (
          <StyledIconButton kind="tertiary" to={mapViewURL}>
            <LineMapIcon />
            <BodyText variant="small">{mapText}</BodyText>
          </StyledIconButton>
        )}
      </InlineContainer>
      {showFilters && <HotelExposedFiltersHeader
        filters={filters}
        overrideAvailableFilters={overrideAvailableFilters}
        showSort
        sortOptions={sortOptions}
        onFilterChange={onFilterChange}
        onSortChange={onSortChange}
      />}
    </Container>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((appState): MappedStateProps => ({
  windowSearch: appState.router.location.search,
}))(SearchField)
